<template>
  <div
    v-if="freebiesAvailable()"
    ref="freebieChooser"
    class="cart-freebies"
    :class="{
      hidden: freebiesSoldout,
    }"
  >
    <div
      class="cart-freebies__queue-title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
    >
      <span v-text="queueTitle" />
    </div>

    <template v-for="freebie of visibleAdjustedFreebies" :key="freebie.handle">
      <div
        v-for="(product, productIndex) of freebie.products"
        :key="
          product
            ? `${product.handle}-${productIndex}`
            : `loading-${productIndex}`
        "
        class="cart-freebies__queue-product-container"
        :class="{
          'cart-freebies__queue-product-container--multivariant':
            freebie.cartVariantSelectorActive,
        }"
      >
        <cart-freebie-card
          class="cart-freebies__queue-product"
          :name="freebieName(freebie.handle, productIndex)"
          :freebie-handle="freebie.handle"
          :freebie-group-uid="freebie.groupUid"
          :product="product"
          :queue-limit="freebie.quantity"
          :queue-size="
            freebie.products.filter((product) => product.selected).length
          "
          :selected="product.selected"
          :show-checkbox="freebie.quantity > 1"
          :is-multiple-variant-card="freebie.cartVariantSelectorActive"
          :freebie-variant-ids="product.freebieVariantIds || null"
          @select-product="
            (variantId, selected) => {
              selectProduct(variantId, freebie, selected);
            }
          "
          @add-variant="(variantId) => addFreebieVariant(variantId, freebie)"
        />
      </div>
    </template>

    <p
      v-if="error"
      class="cart-freebies__error text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      v-text="error"
    />

    <notification-panel
      v-if="error"
      class-container="cart-freebies__error"
      :show-close="false"
      :text="error"
      type="error"
    />

    <div
      v-if="visibleFreebieQuantity > 1"
      class="cart-freebies__queue-footer text-center"
    >
      <btn
        class="cart-freebies__queue-save"
        :label="$string('cart.freebies.save')"
        modifiers="secondary block center"
        :show-state-icon="state === 'updating'"
        size="small"
        type="button"
        @click="handleSaveClick"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Cart freebies (cart-freebies)
 * -----------------------------------------------------------------------------
 * Component to handle loading and choosing campaign freebies.
 *
 *
 */
import { mapActions, mapState } from 'pinia';

import Btn from '~global/btn/btn';
import CartFreebieCard from '~async/cart-freebies/cart-freebie-card';
import NotificationPanel from '~async/utils/notification-panel/notification-panel';

import IconLoading from '~icons/general/loading.svg';

import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useToastNotificationsStore from '~stores/toast-notifications';
import { getFreebieAvailability } from '~/helpers/freebies';
import { formatCartError } from '~/helpers/format-cart-error';

export default {
  name: 'CartFreebies',

  components: {
    Btn,
    CartFreebieCard,
    IconLoading,
    NotificationPanel,
  },

  data() {
    return {
      error: '',
      freebies: [],
      state: 'loading',
      sameFreebiesSeveralTimes: false,
      currentAvailableQuantity: 0,
      freebiesSoldout: false,
      depositItemIds: [],
      freebieAvailable: false,
      currentFreebieIndex: 0,
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      thresholds: 'thresholds',
      codeState: 'codeState',
    }),
    ...mapState(useCartStore, {
      cartItems: (store) => store.response.items,
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Compute freebie products.
     * first check if a product of freebies is available
     * when there is only one product in freebies and quantity is more than 1
     * we need to render the freebie product several times
     * when there are multiple thresholds met
     * we need to merge the products from all thresholds into the products together
     * @returns {Array}
     */
    adjustedFreebies() {
      return this.freebies
        .map((freebie) => {
          if (freebie.quantity > 1 && freebie.products.length === 1) {
            this.sameFreebiesSeveralTimes = true;
            // Duplicate each product according to freebie.quantity
            // we start at 1 as one product is already added
            for (let quantity = 1; quantity < freebie.quantity; quantity++) {
              freebie.products.push(...freebie.products);
            }
          }
          this.currentAvailableQuantity = freebie.quantity;

          // if there are multiple thresholds met adapt quantity
          if (this.freebies.length > 1) {
            this.currentAvailableQuantity = this.freebies.reduce(
              (total, free) => {
                return total + free.quantity;
              },
              0,
            );
          }
          return freebie;
        })
        .filter((freebie) => freebie.products.length > 0);
    },

    /**
     * Shows the first group without an added freebie from group in cart
     * complete = amount of fitting cart items >= needed freebie quantity
     */
    visibleAdjustedFreebies() {
      const groups = this.adjustedFreebies;
      const firstIncomplete = groups.find(
        (freebie) =>
          this.getFittingCartItems(freebie).length < freebie.quantity,
      );
      return firstIncomplete ? [firstIncomplete] : [];
    },

    cartFreebies() {
      return this.cartItems.filter((item) => {
        return item.properties?.[this.$variable('discount.freebies.property')];
      });
    },

    /**
     * Get queue title string.
     */
    queueTitle() {
      if (this.freebies.length === 0) {
        return false;
      }

      return this.$string('cart.freebies.title', {
        pluralise: 1,
        replace: {
          count: this.currentFreebieSelectedCount,
          total: this.currentFreebieTotalQuantity,
        },
      });
    },

    currentFreebieSelectedCount() {
      return this.sumAcrossGroup(
        (freebie) =>
          freebie.products.filter((product) => product.selected).length,
      );
    },

    currentFreebieTotalQuantity() {
      return this.sumAcrossGroup((freebie) => freebie.quantity);
    },

    /**
     * Quantity choosable within the currently visible slot(s) alone, as
     * opposed to currentFreebieTotalQuantity, which is summed across the
     * whole freebie group. Used to decide whether a batch "save selection"
     * step is needed - a single-quantity product slot adds immediately
     * on selection, so it never needs one.
     */
    visibleFreebieQuantity() {
      let total = 0;
      this.visibleAdjustedFreebies.forEach((freebie) => {
        total += freebie.quantity;
      });
      return total;
    },
  },

  watch: {
    /**
     * Watch codeState to trigger reactivity.
     */
    codeState: {
      async handler(newValue) {
        if (newValue === 'ready' && this.freebiesAvailable()) {
          await this.$nextTick();
          this.$refs.freebieChooser.scrollIntoView({ behavior: 'smooth' });
        }
      },
      immediate: true,
    },

    /**
     * Watch thresholds to set freebies array.
     * and adapt tagline
     */
    thresholds: {
      handler() {
        this.setFreebies();
        this.currentAvailableQuantity = 0;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      removeItem: 'updateItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),

    freebieName(handle, freebieIndex) {
      return `${handle}-${freebieIndex}`;
    },

    /**
     * A "Freebie by Product" group with multiple qualifying condition
     * products (or a single one bought several times) is split into one
     * entry per earned unit, all sharing the same groupUid, but only the
     * first incomplete entry is ever visible at once (see
     * visibleAdjustedFreebies). Summing over the visible entry alone would
     * therefore only ever account for 1 earned unit, so we sum over every
     * entry that shares its groupUid instead.
     * @param {Function} getValue - Returns the value to sum for one freebie entry.
     * @returns {Number}
     */
    sumAcrossGroup(getValue) {
      const groupUids = new Set(
        this.visibleAdjustedFreebies.map((freebie) => freebie.groupUid),
      );
      return this.adjustedFreebies
        .filter((freebie) => groupUids.has(freebie.groupUid))
        .reduce((sum, freebie) => sum + getValue(freebie), 0);
    },

    /**
     * Check if there are any available freebies.
     * - Check if not autoAdd
     * - Check if not in cart
     */
    freebiesAvailable() {
      if (!this.freebies.length) {
        return false;
      }

      const needsAttention = this.freebies.some((freebie) => {
        if (!freebie.products || freebie.products.length === 0) return false;
        const fitting = this.getFittingCartItems(freebie).length;
        return fitting < freebie.quantity;
      });

      return needsAttention;
    },

    /**
     * Set freebies.
     */
    setFreebies() {
      this.freebies = this.thresholds
        .filter((threshold) => {
          // if all products are not available we don't show the freebie
          const visibleProducts = threshold.freeProducts.filter((product) => {
            return getFreebieAvailability(product);
          });

          const allSoldOut = visibleProducts.length === 0;

          return (
            (threshold.type === 'threshold' || threshold.type === 'product') &&
            threshold.active &&
            !allSoldOut &&
            !threshold.autoAdd
          );
        })
        .map((threshold) => {
          let products = threshold.freeProducts
            .filter((product) => {
              return (
                getFreebieAvailability(product) ||
                !this.$variable('discount.freebies.hideSoldout')
              );
            })
            .sort((a, b) => {
              if (
                !a.selectedOrFirstAvailableVariant?.available &&
                b.selectedOrFirstAvailableVariant?.available
              ) {
                return 1;
              }

              if (
                a.selectedOrFirstAvailableVariant?.available &&
                !b.selectedOrFirstAvailableVariant?.available
              ) {
                return -1;
              }

              return 0;
            })
            .map((product) => {
              let selected = false;
              this.cartFreebies.forEach((freebie) => {
                const freebieHandle =
                  freebie.properties?.[
                    this.$variable('discount.freebies.property')
                  ];

                if (freebieHandle === threshold.handle) {
                  selected =
                    selected ||
                    freebie.id === product.selectedOrFirstAvailableVariant?.id;
                }
              });

              return { ...product, selected };
            });

          if (threshold.cartVariantSelectorActive) {
            products = this.buildMultiVariantProducts(
              products,
              threshold.freeProducts,
            );
          }

          this.state = 'ready';

          return {
            ...threshold,
            products,
            queue: [],
            thresholdGroup: [],
            inCart: this.getFittingCartItems(threshold).length > 0,
          };
        });
    },

    selectProduct(variantId, freebie, selected) {
      const unselectOthers = freebie.quantity === 1 && selected;
      freebie.products.forEach((freebieProduct) => {
        const isMatch =
          freebie.cartVariantSelectorActive && freebieProduct.freebieVariantIds
            ? freebieProduct.freebieVariantIds.includes(variantId.toString())
            : freebieProduct.selectedOrFirstAvailableVariant?.id === variantId;

        if (isMatch) {
          freebieProduct.selected = selected;
          if (selected && freebie.cartVariantSelectorActive) {
            const newVariant = freebieProduct.variants?.find(
              (fv) =>
                fv.id === variantId ||
                fv.id.toString() === variantId.toString(),
            );
            if (newVariant) {
              freebieProduct.selectedOrFirstAvailableVariant = newVariant;
            }
          }
        } else if (unselectOthers) {
          freebieProduct.selected = false;
        }
      });
    },

    /**
     * Build deduplicated products for multi-variant card mode.
     * Groups products by handle, collecting all configured variant IDs.
     * Removes products where all configured variants are unavailable.
     * @param {Array} filteredProducts - Products after availability filter + selected mapping.
     * @param {Array} allFreeProducts - All freeProducts from threshold (unfiltered).
     */
    buildMultiVariantProducts(filteredProducts, allFreeProducts) {
      const deduped = [];

      allFreeProducts.forEach((rawProduct) => {
        const variantId =
          rawProduct.selectedOrFirstAvailableVariant?.id?.toString();
        if (!variantId) return;

        const existingEntry = deduped.find(
          (prod) => prod.handle === rawProduct.handle,
        );
        if (existingEntry) {
          if (!existingEntry.freebieVariantIds.includes(variantId)) {
            existingEntry.freebieVariantIds.push(variantId);
          }
        } else {
          const filteredMatch = filteredProducts.find(
            (prod) => prod.handle === rawProduct.handle,
          );
          const baseProduct = filteredMatch || rawProduct;
          deduped.push({
            ...baseProduct,
            freebieVariantIds: [variantId],
          });
        }
      });

      // If a filtered (available) product marked as selected, preserve that state
      filteredProducts.forEach((product) => {
        if (!product.selected) return;
        const entry = deduped.find((prod) => prod.handle === product.handle);
        if (entry) {
          entry.selected = true;
          entry.selectedOrFirstAvailableVariant =
            product.selectedOrFirstAvailableVariant;
        }
      });

      // Remove products where none of the configured variants are available
      return deduped.filter((product) => {
        return product.freebieVariantIds.some((vid) => {
          const variant = product.variants?.find(
            (fv) => fv.id.toString() === vid,
          );
          return (
            variant &&
            (variant.available ||
              (product.allowHiddenVariants &&
                variant.availableState === 'hidden' &&
                variant.inventory > 0))
          );
        });
      });
    },

    /**
     * Directly add a selected freebie variant to cart (multi-variant single-qty flow).
     * @param {Number} variantId - Selected variant ID.
     * @param {Object} freebie - Freebie threshold data.
     */
    async addFreebieVariant(variantId, freebie) {
      const product = freebie.products.find((prod) =>
        prod.freebieVariantIds?.includes(variantId.toString()),
      );
      if (!product) return;

      const variant = product.variants?.find(
        (fv) =>
          fv.id === variantId || fv.id.toString() === variantId.toString(),
      );
      if (!variant) return;

      this.state = 'updating';

      await this.addItem([
        {
          properties: {
            [this.$variable('discount.freebies.property')]: freebie.handle,
            [this.$variable('discount.freebies.propertyGroup')]:
              freebie.groupUid,
          },
          quantity: 1,
          id: variantId,
          deposit:
            variant.canDeposit && this.$variable('store.country') === 'DE'
              ? variant.canDepositAmount
              : 0,
          hasLocalizedImage: Boolean(variant.localizedImage),
        },
      ]);

      this.state = 'ready';

      const toastNotificationsStore = useToastNotificationsStore();
      toastNotificationsStore.addToast({
        id: 'freebie-manual-add-success',
        type: 'success',
        customClass: 'drawer',
        showCheckMark: false,
        title: this.$string('cart.freebies.added_message'),
      });
    },

    /**
     * Reset the queue and close the freebie
     */
    closeFreebie() {
      this.freebies.forEach((freebie) => {
        freebie.queue = [];
      });
    },

    getFittingCartItems(freebie) {
      return this.cartFreebies.filter(
        (cartFreebie) =>
          cartFreebie.properties?.[
            this.$variable('discount.freebies.property')
          ] === freebie.handle,
      );
    },

    /**
     * Handle save click.
     * Uses promise to avoid race-condition in adding/removing
     * @param {Object} freebie - Current freebie.
     */
    async handleSaveClick() {
      const freebiesToRemove = [];
      const freebiesToAdd = [];

      /* Check if products are in cart, that are no longer selected
       * these have to be removed
       * also checks for deposit
       */
      this.cartFreebies.forEach((cartFreebie) => {
        const fittingFreebie = this.freebies.find(
          (freebie) =>
            freebie.handle ===
            cartFreebie.properties?.[
              this.$variable('discount.freebies.property')
            ],
        );

        if (!fittingFreebie) {
          return;
        }

        const selectedProduct = fittingFreebie.products.find(
          (product) =>
            product.selected &&
            product.selectedOrFirstAvailableVariant.id === cartFreebie.id,
        );

        if (!selectedProduct) {
          freebiesToRemove.push({
            id: cartFreebie.key,
            quantity: 0,
            deposit:
              cartFreebie.can_deposit &&
              this.$variable('store.country') === 'DE'
                ? cartFreebie.can_deposit_amount * cartFreebie.quantity
                : 0,
          });
        }
      });

      /* Check if products that are selected are not in the cart
       * these have to be added
       * also checks for deposit
       */
      this.freebies.forEach((freebie) => {
        const fittingCartItems = this.getFittingCartItems(freebie);

        freebie.products.forEach((product) => {
          const selectedOrFirstAvailableVariant =
            product.selectedOrFirstAvailableVariant;

          if (
            product.selected &&
            !fittingCartItems.some(
              (item) => item.id === selectedOrFirstAvailableVariant.id,
            )
          ) {
            freebiesToAdd.push({
              properties: {
                [this.$variable('discount.freebies.property')]: freebie.handle,
                [this.$variable('discount.freebies.propertyGroup')]:
                  freebie.groupUid,
              },
              quantity: 1,
              id: selectedOrFirstAvailableVariant.id,
              deposit:
                selectedOrFirstAvailableVariant.canDeposit &&
                this.$variable('store.country') === 'DE'
                  ? selectedOrFirstAvailableVariant.canDepositAmount
                  : 0,
              hasLocalizedImage: Boolean(
                selectedOrFirstAvailableVariant.localizedImage,
              ),
            });
          }
        });
      });

      const depositAmountToRemove = freebiesToRemove.reduce(
        (sum, item) => sum + (item.deposit || 0),
        0,
      );

      const depositAmountToAdd = freebiesToAdd.reduce(
        (sum, item) => sum + (parseInt(item.deposit) || 0),
        0,
      );

      const depositAmount = depositAmountToAdd - depositAmountToRemove;
      let depositProductIds = [];

      if (depositAmount !== 0) {
        depositProductIds = [...freebiesToRemove, ...freebiesToAdd]
          .filter((item) => item.deposit > 0)
          .map((item) => item.id);

        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          ...depositProductIds,
        ]);
      }

      if (freebiesToRemove.length + freebiesToAdd.length > 0) {
        this.state = 'updating';
      }

      // first we need to remove, so we don't have double items in the cart
      if (freebiesToRemove.length > 0) {
        await this.removeItem(freebiesToRemove);
      }

      if (freebiesToAdd.length > 0) {
        await this.addItem(freebiesToAdd);
      }

      if (depositAmount !== 0) {
        if (depositAmount > 0) {
          await this.addItem([
            {
              id: this.$variable('product.depositProductID'),
              quantity: depositAmount,
            },
          ]);
        } else {
          const cartDepositItem = this.cartItems.find(
            (item) =>
              item.id === parseInt(this.$variable('product.depositProductID')),
          );
          await this.removeItem([
            {
              id: cartDepositItem.key,
              quantity: cartDepositItem.quantity + depositAmount,
            },
          ]);
        }

        this.setDepositItemUpdate(
          this.depositItemUpdate.filter(
            (id) => !depositProductIds.includes(id),
          ),
        );
      }

      this.state = 'ready';

      // shows success message once freebie was added
      if (freebiesToAdd.length > 0) {
        const toastNotificationsStore = useToastNotificationsStore();
        toastNotificationsStore.addToast({
          id: 'freebie-manual-add-success',
          type: 'success',
          customClass: 'drawer',
          showCheckMark: false,
          title: this.$string('cart.freebies.added_message'),
        });
      }
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     * @returns {String}
     */
    handleErrorMessage(errorDescription) {
      return formatCartError(errorDescription, '<br>');
    },
  },
};
</script>

<style lang="scss">
@import './cart-freebies';
</style>
